import React, { useCallback, useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import KeepImportDialog from "./KeepImportDialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import {
  Globe,
  Facebook,
  Instagram,
  Youtube,
  MapPin,
  Send,
  FolderOpen,
  Plus,
  Eye,
  EyeOff,
  Save,
  X,
  NotebookPen,
  Lock,
  ExternalLink,
  Music2,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Trash2,
} from "lucide-react";

const LINK_FIELDS = [
  { key: "website", label: "Sito web", icon: Globe, placeholder: "https://..." },
  { key: "instagram", label: "Instagram", icon: Instagram, placeholder: "https://instagram.com/..." },
  { key: "facebook", label: "Facebook", icon: Facebook, placeholder: "https://facebook.com/..." },
  { key: "tiktok", label: "TikTok", icon: Music2, placeholder: "https://tiktok.com/@..." },
  { key: "youtube", label: "YouTube", icon: Youtube, placeholder: "https://youtube.com/..." },
  { key: "google", label: "Google Business", icon: MapPin, placeholder: "https://g.co/kgs/..." },
  { key: "dropbox", label: "Cartella Dropbox", icon: FolderOpen, placeholder: "https://www.dropbox.com/..." },
  { key: "telegram", label: "Chat Telegram", icon: Send, placeholder: "https://t.me/..." },
];

const EMPTY_LINKS = Object.fromEntries(LINK_FIELDS.map((f) => [f.key, ""]));

export default function ClientRubricCard({ clientId, initialProfile, initialClient, onClientUpdate, onProfileUpdate }) {
  const [profile, setProfile] = useState(
    initialProfile || { positioning: "", links: EMPTY_LINKS, notes: [] }
  );
  const [positioning, setPositioning] = useState(initialProfile?.positioning || "");
  const [links, setLinks] = useState({ ...EMPTY_LINKS, ...(initialProfile?.links || {}) });
  const [city, setCity] = useState(initialProfile?.city || "");
  const [province, setProvince] = useState(initialProfile?.province || "");
  const [nameSalone, setNameSalone] = useState(initialClient?.name_salone || "");
  const [refNome, setRefNome] = useState(initialClient?.referente_nome || "");
  const [refCognome, setRefCognome] = useState(initialClient?.referente_cognome || "");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [keepOpen, setKeepOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [expanding, setExpanding] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/staff/clients/${clientId}/profile`);
      setProfile(data.profile);
      setPositioning(data.profile.positioning || "");
      setLinks({ ...EMPTY_LINKS, ...(data.profile.links || {}) });
      setCity(data.profile.city || "");
      setProvince(data.profile.province || "");
      setDirty(false);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [clientId]);

  useEffect(() => {
    if (!initialProfile) refresh();
  }, [initialProfile, refresh]);

  // Sync top-level fields when parent updates the client (e.g. after approve/reject)
  useEffect(() => {
    if (initialClient) {
      setNameSalone(initialClient.name_salone || "");
      setRefNome(initialClient.referente_nome || "");
      setRefCognome(initialClient.referente_cognome || "");
    }
  }, [initialClient]);

  const onLinkChange = (key, value) => {
    setLinks((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const onPositioningChange = (value) => {
    setPositioning(value);
    setDirty(true);
  };

  const onAnagraphField = (setter) => (value) => {
    setter(value);
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        positioning,
        links,
        city: city.trim(),
        province: province.trim().toUpperCase().slice(0, 4),
      };
      // Only include top-level fields if non-empty (validators reject empty strings)
      if (nameSalone.trim()) payload.name_salone = nameSalone.trim();
      if (refNome.trim()) payload.referente_nome = refNome.trim();
      if (refCognome.trim()) payload.referente_cognome = refCognome.trim();
      const { data } = await api.patch(`/staff/clients/${clientId}/profile`, payload);
      setProfile(data.profile);
      setLinks({ ...EMPTY_LINKS, ...(data.profile.links || {}) });
      setPositioning(data.profile.positioning || "");
      setCity(data.profile.city || "");
      setProvince(data.profile.province || "");
      if (data.user) {
        setNameSalone(data.user.name_salone || "");
        setRefNome(data.user.referente_nome || "");
        setRefCognome(data.user.referente_cognome || "");
        if (onClientUpdate) onClientUpdate(data.user);
      }
      setDirty(false);
      toast.success("Scheda salvata");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const expandPositioning = async () => {
    if (dirty) {
      const ok = window.confirm(
        "Prima di espandere salvo le modifiche correnti. Procedo?"
      );
      if (!ok) return;
      await save();
    }
    if (!positioning.trim()) {
      toast.error("Scrivi prima qualche riga di posizionamento.");
      return;
    }
    if (
      !window.confirm(
        "L'AI sostituirà il posizionamento attuale con una versione più dettagliata in 4 paragrafi. Procedo?"
      )
    ) {
      return;
    }
    setExpanding(true);
    try {
      const { data } = await api.post(
        `/staff/clients/${clientId}/positioning/expand`
      );
      setProfile(data.profile);
      setPositioning(data.profile.positioning || "");
      setDirty(false);
      toast.success("Posizionamento espanso con AI");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setExpanding(false);
    }
  };

  return (
    <div
      data-testid="client-rubric-card"
      className="rounded-3xl border border-[var(--mp-ink)] bg-white shadow-brutal-sm overflow-hidden"
    >
      <div className="px-6 sm:px-8 py-5 border-b border-[var(--mp-border)] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <button
          type="button"
          data-testid="rubric-collapse-toggle"
          onClick={() => setCollapsed((v) => !v)}
          className="flex-1 text-left cursor-pointer group"
          aria-expanded={!collapsed}
        >
          <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-[var(--mp-muted)]">
            Rubrica cliente
          </div>
          <div className="font-display text-2xl tracking-tight mt-1 flex items-center gap-2">
            Scheda brand &amp; link
            {collapsed ? (
              <ChevronDown className="h-5 w-5 text-[var(--mp-muted)] group-hover:text-[var(--mp-ink)]" />
            ) : (
              <ChevronUp className="h-5 w-5 text-[var(--mp-muted)] group-hover:text-[var(--mp-ink)]" />
            )}
          </div>
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            data-testid="rubric-keep-import-btn"
            onClick={() => setKeepOpen(true)}
            variant="outline"
            className="rounded-full border-[var(--mp-ink)]"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            Importa da Google Keep
          </Button>
          {dirty && !collapsed && (
            <Button
              data-testid="rubric-save-btn"
              disabled={saving}
              onClick={save}
              className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Salvataggio…" : "Salva modifiche"}
            </Button>
          )}
        </div>
      </div>

      {!collapsed && (
        <div className="p-6 sm:p-8 space-y-8">
        {/* Anagrafica */}
        <div className="space-y-3">
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
            Anagrafica
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <AnagField
              testId="rubric-name-salone"
              label="Nome salone"
              value={nameSalone}
              onChange={onAnagraphField(setNameSalone)}
              placeholder="Es. Salone Bellaria"
            />
            <div className="grid grid-cols-[1fr_88px] gap-3">
              <AnagField
                testId="rubric-city"
                label="Città"
                value={city}
                onChange={onAnagraphField(setCity)}
                placeholder="Es. Pavia"
              />
              <AnagField
                testId="rubric-province"
                label="Prov."
                value={province}
                onChange={(v) => onAnagraphField(setProvince)(v.toUpperCase().slice(0, 4))}
                placeholder="PV"
              />
            </div>
            <AnagField
              testId="rubric-referente-nome"
              label="Nome referente"
              value={refNome}
              onChange={onAnagraphField(setRefNome)}
              placeholder="Es. Mario"
            />
            <AnagField
              testId="rubric-referente-cognome"
              label="Cognome referente"
              value={refCognome}
              onChange={onAnagraphField(setRefCognome)}
              placeholder="Es. Rossi"
            />
          </div>
          <div className="text-[11px] text-[var(--mp-muted)]">
            Modifica i dati anagrafici del cliente. Per aggiornare l'email contatta il supporto.
          </div>
        </div>

        {/* Posizionamento */}
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <label className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
              Posizionamento
            </label>
            <Button
              type="button"
              size="sm"
              variant="outline"
              data-testid="rubric-expand-positioning"
              onClick={expandPositioning}
              disabled={expanding || !positioning.trim()}
              className="rounded-full border-[var(--mp-ink)] h-7 px-3 text-[11px] font-semibold"
              title="Genera una versione più ricca del posizionamento, alimenta caption e idee storie"
            >
              <Sparkles className="h-3.5 w-3.5 mr-1.5" />
              {expanding ? "Espando…" : "Espandi con AI"}
            </Button>
          </div>
          <Textarea
            data-testid="rubric-positioning"
            value={positioning}
            onChange={(e) => onPositioningChange(e.target.value)}
            placeholder="Es: Salone per clientela 30-50 anni, tono professionale ma simpatico, specializzato in colore e extension…"
            className="min-h-[180px] resize-y rounded-2xl border-[var(--mp-border)]"
          />
          <div className="text-[11px] text-[var(--mp-muted)]">
            Usato dall'AI per generare caption e idee coerenti con il brand. Il pulsante "Espandi con AI" lo riscrive in 4 paragrafi più dettagliati.
          </div>
        </div>

        {/* Links */}
        <div className="space-y-3">
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
            Link &amp; canali
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {LINK_FIELDS.map(({ key, label, icon: Icon, placeholder }) => (
              <LinkField
                key={key}
                fieldKey={key}
                label={label}
                icon={Icon}
                placeholder={placeholder}
                value={links[key] || ""}
                onChange={(v) => onLinkChange(key, v)}
              />
            ))}
          </div>
        </div>

        {/* Metricool brand link */}
        <MetricoolBrandSelector
          clientId={clientId}
          currentBlogId={initialClient?.metricool_blog_id || null}
          onChanged={(newId) => onClientUpdate && onClientUpdate({ ...initialClient, metricool_blog_id: newId })}
        />

        {/* Notes */}
        <NotesSection
          clientId={clientId}
          notes={profile.notes || []}
          onChange={(p) => setProfile(p)}
        />
      </div>
      )}

      <KeepImportDialog
        clientId={clientId}
        open={keepOpen}
        onOpenChange={setKeepOpen}
        onImported={(p) => {
          setProfile(p);
          setLinks({ ...EMPTY_LINKS, ...(p.links || {}) });
          setPositioning(p.positioning || "");
          setDirty(false);
        }}
      />
    </div>
  );
}

function AnagField({ testId, label, value, onChange, placeholder }) {
  return (
    <div className="rounded-2xl border border-[var(--mp-border)] bg-[var(--mp-dash-bg)] p-3">
      <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
        {label}
      </div>
      <Input
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-2 border-[var(--mp-border)] bg-white rounded-full h-9 text-sm"
      />
    </div>
  );
}

function LinkField({ fieldKey, label, icon: Icon, placeholder, value, onChange }) {
  const hasValue = Boolean(value && value.trim());
  return (
    <div className="rounded-2xl border border-[var(--mp-border)] bg-[var(--mp-dash-bg)] p-3">
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="mt-2 flex items-center gap-2">
        <Input
          data-testid={`rubric-link-${fieldKey}`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="border-[var(--mp-border)] bg-white rounded-full h-9 text-sm"
        />
        {hasValue && (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`rubric-link-open-${fieldKey}`}
            className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-[var(--mp-ink)] text-white hover:bg-black shrink-0"
            title={`Apri ${label}`}
          >
            <ExternalLink className="h-4 w-4" />
          </a>
        )}
      </div>
    </div>
  );
}

function NotesSection({ clientId, notes, onChange }) {
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newContent, setNewContent] = useState("");
  const [newSensitive, setNewSensitive] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const add = async () => {
    if (!newTitle.trim()) {
      toast.error("Inserisci un titolo");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/staff/clients/${clientId}/notes`, {
        title: newTitle,
        content: newContent,
        is_sensitive: newSensitive,
      });
      onChange(data.profile);
      setNewTitle("");
      setNewContent("");
      setNewSensitive(true);
      setCreating(false);
      toast.success("Nota aggiunta");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)] flex items-center gap-2">
          <NotebookPen className="h-3.5 w-3.5" /> Note &amp; password
        </div>
        {!creating && (
          <Button
            size="sm"
            data-testid="rubric-add-note-btn"
            onClick={() => setCreating(true)}
            className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
          >
            <Plus className="h-4 w-4 mr-1" /> Nuova nota
          </Button>
        )}
      </div>

      {creating && (
        <div
          data-testid="rubric-new-note-form"
          className="rounded-2xl border border-[var(--mp-ink)] bg-[var(--mp-yellow)]/30 p-4 space-y-3"
        >
          <Input
            data-testid="rubric-new-note-title"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Titolo (es: Password Instagram)"
            className="bg-white border-[var(--mp-ink)] rounded-full"
          />
          <Textarea
            data-testid="rubric-new-note-content"
            value={newContent}
            onChange={(e) => setNewContent(e.target.value)}
            placeholder="Contenuto della nota…"
            className="bg-white border-[var(--mp-ink)] rounded-2xl min-h-[120px]"
          />
          <label className="flex items-center gap-2 text-sm text-[var(--mp-ink)]">
            <input
              type="checkbox"
              checked={newSensitive}
              onChange={(e) => setNewSensitive(e.target.checked)}
              data-testid="rubric-new-note-sensitive"
              className="h-4 w-4"
            />
            <Lock className="h-3.5 w-3.5" /> Contiene dati riservati (nascondi di default)
          </label>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              data-testid="rubric-new-note-save"
              disabled={submitting}
              onClick={add}
              className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
            >
              <Save className="h-4 w-4 mr-1" />
              {submitting ? "Salvataggio…" : "Salva nota"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              data-testid="rubric-new-note-cancel"
              onClick={() => {
                setCreating(false);
                setNewTitle("");
                setNewContent("");
                setNewSensitive(true);
              }}
              className="rounded-full border-[var(--mp-ink)]"
            >
              <X className="h-4 w-4 mr-1" /> Annulla
            </Button>
          </div>
        </div>
      )}

      {notes.length === 0 && !creating && (
        <div className="rounded-2xl border border-dashed border-[var(--mp-border)] bg-[var(--mp-dash-bg)] p-6 text-center text-[var(--mp-muted)] text-sm">
          Nessuna nota per questo cliente. Usa "Nuova nota" per salvare password,
          appunti o promemoria importati da Google Keep.
        </div>
      )}

      <div className="space-y-3">
        {notes.map((n) => (
          <NoteItem
            key={n.id}
            clientId={clientId}
            note={n}
            onChange={onChange}
          />
        ))}
      </div>
    </div>
  );
}

function NoteItem({ clientId, note, onChange }) {
  const [revealed, setRevealed] = useState(!note.is_sensitive);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(note.title);
  const [content, setContent] = useState(note.content);
  const [sensitive, setSensitive] = useState(Boolean(note.is_sensitive));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setRevealed(!note.is_sensitive);
    setTitle(note.title);
    setContent(note.content);
    setSensitive(Boolean(note.is_sensitive));
  }, [note.id, note.is_sensitive, note.title, note.content]);

  const saveEdit = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(
        `/staff/clients/${clientId}/notes/${note.id}`,
        { title, content, is_sensitive: sensitive }
      );
      onChange(data.profile);
      setEditing(false);
      toast.success("Nota aggiornata");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Eliminare la nota "${note.title}"?`)) return;
    try {
      const { data } = await api.delete(
        `/staff/clients/${clientId}/notes/${note.id}`
      );
      onChange(data.profile);
      toast.success("Nota eliminata");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const copyContent = async () => {
    try {
      await navigator.clipboard.writeText(note.content || "");
      toast.success("Copiato negli appunti");
    } catch {
      toast.error("Copia non riuscita");
    }
  };

  if (editing) {
    return (
      <div
        data-testid={`rubric-note-${note.id}`}
        className="rounded-2xl border border-[var(--mp-ink)] bg-white p-4 space-y-3"
      >
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="border-[var(--mp-ink)] rounded-full"
          data-testid={`rubric-note-edit-title-${note.id}`}
        />
        <Textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          className="border-[var(--mp-ink)] rounded-2xl min-h-[140px]"
          data-testid={`rubric-note-edit-content-${note.id}`}
        />
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={sensitive}
            onChange={(e) => setSensitive(e.target.checked)}
            className="h-4 w-4"
          />
          <Lock className="h-3.5 w-3.5" /> Dati riservati
        </label>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={saveEdit}
            disabled={saving}
            className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
            data-testid={`rubric-note-save-${note.id}`}
          >
            <Save className="h-4 w-4 mr-1" />
            {saving ? "Salvataggio…" : "Salva"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setEditing(false);
              setTitle(note.title);
              setContent(note.content);
              setSensitive(Boolean(note.is_sensitive));
            }}
            className="rounded-full border-[var(--mp-ink)]"
          >
            <X className="h-4 w-4 mr-1" /> Annulla
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid={`rubric-note-${note.id}`}
      className="rounded-2xl border border-[var(--mp-border)] bg-white p-4 space-y-2"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg tracking-tight flex items-center gap-2">
            {note.is_sensitive && <Lock className="h-4 w-4 text-[var(--mp-primary)]" />}
            <span className="truncate">{note.title}</span>
          </div>
          {note.updated_at && (
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--mp-muted)] mt-1">
              Aggiornata il{" "}
              {new Date(note.updated_at).toLocaleDateString("it-IT")}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {note.is_sensitive && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setRevealed((v) => !v)}
              data-testid={`rubric-note-toggle-${note.id}`}
              className="h-8 w-8 p-0"
              title={revealed ? "Nascondi" : "Mostra"}
            >
              {revealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setEditing(true)}
            className="h-8 px-2 text-xs"
            data-testid={`rubric-note-edit-${note.id}`}
          >
            Modifica
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={remove}
            className="h-8 w-8 p-0 text-[var(--mp-primary)]"
            data-testid={`rubric-note-delete-${note.id}`}
            title="Elimina"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {note.content && (
        <div className="rounded-xl bg-[var(--mp-dash-bg)] border border-[var(--mp-border)] p-3">
          {revealed ? (
            <>
              <pre
                data-testid={`rubric-note-content-${note.id}`}
                className="font-mono text-sm whitespace-pre-wrap break-words"
              >
                {note.content}
              </pre>
              <div className="mt-2 flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={copyContent}
                  className="rounded-full border-[var(--mp-ink)] h-7 text-xs"
                  data-testid={`rubric-note-copy-${note.id}`}
                >
                  Copia
                </Button>
                {note.is_sensitive && (
                  <Badge className="rounded-full bg-[var(--mp-primary)] text-white text-[10px] uppercase tracking-[0.2em]">
                    Riservata
                  </Badge>
                )}
              </div>
            </>
          ) : (
            <div className="text-sm text-[var(--mp-muted)] flex items-center gap-2">
              <Lock className="h-4 w-4" /> Contenuto nascosto — clicca l'occhio
              per mostrare.
            </div>
          )}
        </div>
      )}
    </div>
  );
}


const _metricoolBrandsCache = { data: null, at: 0 };

function MetricoolBrandSelector({ clientId, currentBlogId, onChanged }) {
  const [brands, setBrands] = useState(_metricoolBrandsCache.data || []);
  const [loading, setLoading] = useState(!_metricoolBrandsCache.data);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState(currentBlogId ? String(currentBlogId) : "");

  useEffect(() => {
    setSelectedId(currentBlogId ? String(currentBlogId) : "");
  }, [currentBlogId]);

  useEffect(() => {
    let mounted = true;
    // Reuse cached list (TTL 5 min) to avoid N requests when many cards are open
    const now = Date.now();
    if (_metricoolBrandsCache.data && now - _metricoolBrandsCache.at < 5 * 60 * 1000) {
      setBrands(_metricoolBrandsCache.data);
      setLoading(false);
      return () => { mounted = false; };
    }
    api
      .get("/admin/metricool/brands")
      .then(({ data }) => {
        if (!mounted) return;
        const list = data.brands || [];
        _metricoolBrandsCache.data = list;
        _metricoolBrandsCache.at = Date.now();
        setBrands(list);
      })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const blogId = selectedId ? Number(selectedId) : null;
      await api.patch(`/admin/clients/${clientId}/metricool`, { blog_id: blogId });
      toast.success(blogId ? "Brand Metricool collegato" : "Collegamento Metricool rimosso");
      onChanged && onChanged(blogId);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const current = brands.find((b) => String(b.id) === selectedId);

  return (
    <div className="space-y-2 border-t border-[var(--mp-border)] pt-4">
      <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--mp-muted)]">
        Brand Metricool
      </div>
      {loading ? (
        <div className="text-xs text-[var(--mp-muted)]">Carico brand…</div>
      ) : brands.length === 0 ? (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-2">
          Nessun brand Metricool trovato. Verifica le credenziali in Impostazioni.
        </div>
      ) : (
        <div className="flex items-center gap-2 flex-wrap">
          <select
            data-testid="rubric-metricool-select"
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            className="h-10 px-3 rounded-full border border-[var(--mp-border)] bg-white text-sm flex-1 min-w-[200px]"
          >
            <option value="">— Non collegato —</option>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>
                {b.label} {b.instagram ? `(@${b.instagram})` : ""}
              </option>
            ))}
          </select>
          <Button
            onClick={save}
            disabled={saving || String(currentBlogId || "") === selectedId}
            data-testid="rubric-metricool-save"
            className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
          >
            <Save className="h-4 w-4 mr-1" />
            {saving ? "Salvo…" : "Collega"}
          </Button>
        </div>
      )}
      {current && (
        <div className="text-[11px] text-[var(--mp-muted)] flex flex-wrap gap-2">
          {current.instagram && <span>IG @{current.instagram}</span>}
          {current.facebook && <span>FB {current.facebook}</span>}
          {current.tiktok && <span>TikTok @{current.tiktok}</span>}
        </div>
      )}
    </div>
  );
}
